import { useState, useEffect, useRef, Fragment } from 'react';
import { join as j, currencyUi } from '../data';
import { buildIgPayment, calcFeeCoverAmount, newPaymentToken } from '../igIframe';
import { logDonationEvent } from '../api';
import { useAbandonedForm } from '../hooks/useAbandonedForm';
import { useIgFrameHeight } from '../hooks/useIgFrameHeight';
import { useBitRedirect } from '../hooks/useBitRedirect';
import { readUtmParams } from '../utmParams';
import { resolvePurchaseValue, trackPurchase } from '../analytics';
import ThankYouModal from '../components/ThankYouModal';
import CurrencyPicker from '../components/CurrencyPicker';
import ForeignAddressFields from '../components/ForeignAddressFields';
import { useConsentTermsDialog, TermsLink } from '../components/ConsentTerms';
import {
  DEFAULT_CURRENCY, currencyMeta, formatAmount, presetAmounts,
  needsAddress, countryName,
} from '../currencies';

const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 90 }, (_, i) => String(CURRENT_YEAR - 16 - i));

// Phrases rendered in the brand purple within the otherwise-black content text.
// cls 'join-em' = bold, 18px; 'join-em join-em--plain' = purple but body size/weight.
const EMPHASIS = [
  // first Hebrew + first Arabic rows: bold purple
  { phrase: 'התקווה לבנות מציאות טובה יותר.', cls: 'join-em join-em--strong' },
  { phrase: 'الأمل في بناء واقع أفضل.', cls: 'join-em join-em--strong' },
  // the rest: regular purple
  { phrase: 'גם תרומה חודשית קטנה, עושה שינוי גדול.', cls: 'join-em join-em--plain' },
  { phrase: 'حتى مساهمة شهرية صغيرة تصنع تغييرًا كبيرًا.', cls: 'join-em' },
];

function withEmphasis(text) {
  for (const { phrase, cls } of EMPHASIS) {
    const idx = text.indexOf(phrase);
    if (idx !== -1) {
      return [
        text.slice(0, idx),
        <span className={cls} key={phrase}>{phrase}</span>,
        text.slice(idx + phrase.length),
      ];
    }
  }
  return text;
}

// Split on "\n" into separate lines (rendered with <br>), keeping emphasis.
function renderText(text) {
  return text.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {withEmphasis(line)}
    </Fragment>
  ));
}

const emptyForm = {
  firstName: '', lastName: '', address: '', city: '', phone: '', birthYear: '',
  email: '', isStudent: false, institution: '',
  // Foreign-currency memberships only — the street address above is already
  // collected in every currency, so only these two are extra.
  zipCode: '', countryId: '',
};

// Field validation, transcribed from the real Wix join page (exs97).
// `key` maps to our form state; `students` = only required when "I'm a student" is checked.
const VALIDATIONS = [
  { key: 'firstName', pattern: /^\D{2,}$/, message: 'יש להזין שם פרטי يرجى اضافة الاسم للشخصي' },
  { key: 'lastName', pattern: /^\D{2,}$/, message: 'יש להזין שם משפחה يرجى اضافة اسم العائلة' },
  { key: 'address', pattern: /^.{2,}$/, message: 'יש להזין כתובת يرجى اضافة عنوان' },
  { key: 'city', pattern: /^.{2,}$/, message: 'יש להזין יישוב מגורים يرجى اضافة مكان السكنى' },
  {
    key: 'phone',
    pattern: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{1,3})?[-. )]*(\d{2,3})[-. ]*(\d{1,4})(?:\s*(\d+))?[-. ]*(\d{2,4})\s*$/,
    customTest: (v) => (v.match(/\d/g) || []).length >= 9,
    message: 'יש להזין מספר טלפון תקין يرجى اضافة رقم هاتف من صحيح',
  },
  {
    key: 'email',
    pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    message: 'יש להזין כתובת אימייל תקינה يرجى اضافة بريد الكتروني صحيح',
  },
  { key: 'institution', pattern: /^.{2,}$/, students: true, message: 'יש להזין מוסד אקדמי يرجى اضافة مؤسسة أكاديميّة' },
];

// Only asked when the membership is charged in a foreign currency, because
// that's the only time IsraelGives requires them (see ForeignAddressFields).
const FOREIGN_VALIDATIONS = [
  { key: 'zipCode', pattern: /^.{2,7}$/, message: currencyUi.errors.zipCode.he },
  { key: 'countryId', pattern: /^\d+$/, message: currencyUi.errors.country.he },
];

function validateForm(form, currency = DEFAULT_CURRENCY) {
  const errors = {};
  const rules = needsAddress(currency) ? [...VALIDATIONS, ...FOREIGN_VALIDATIONS] : VALIDATIONS;
  for (const f of rules) {
    if (f.students && !form.isStudent) continue;
    const value = form[f.key] || '';
    const ok = f.pattern.test(value) && (!f.customTest || f.customTest(value));
    if (!ok) errors[f.key] = f.message;
  }
  return errors;
}

// The amount grid (preset buttons + "other" input/next), with the currency
// picker above it. Owns its own custom state and calls onChoose(value) when an
// amount is picked.
//
// Membership is a monthly charge, and the picker offers every currency for it
// — the same list the one-time donate page shows. It used to be cut to three
// (ILS, EUR, AUD) to match a per-account IsraelGives flag; currencies.js has
// the flag, the reasoning, and how to restore the cut.
function AmountSelector({ selected, onChoose, currency, onCurrencyChange, id }) {
  const [custom, setCustom] = useState('');

  // The "other" amount field: a value below this threshold is invalid (red border)
  // and blocks the "next" button.
  const OTHER_MIN = currencyMeta(currency).min;
  const customNum = Number(custom);
  const customInvalid = custom.trim() !== '' && (!Number.isFinite(customNum) || customNum < OTHER_MIN);
  // Only reveal the "next" button for a valid amount; an invalid one just shows
  // the input's red border (no button).
  const showNext = custom.trim() !== '' && !customInvalid;
  const submitCustom = () => {
    if (customInvalid) return;
    onChoose(custom);
  };

  return (
    <div className="join-amounts">
      <CurrencyPicker
        id={id}
        value={currency}
        onChange={(code) => { setCustom(''); onCurrencyChange(code); }}
      />
      {presetAmounts(currency, true, j.monthlyAmounts).map((a) => (
        <button
          key={a}
          className={`join-amount ${selected === a ? 'active' : ''}`}
          onClick={() => onChoose(a)}
        >
          {formatAmount(a, currency)}
        </button>
      ))}
      <div className={`join-other-row${showNext ? ' join-other-row--active' : ''}`}>
        <input
          className={`join-amount join-other${customInvalid ? ' join-other--invalid' : ''}`}
          type="number"
          min={OTHER_MIN}
          inputMode="numeric"
          placeholder={j.other.he}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitCustom();
            }
          }}
        />
        {showNext ? (
          <button
            type="button"
            className="join-amount join-next"
            onClick={submitCustom}
          >
            {j.next.he} {j.next.ar}
          </button>
        ) : (
          <img
            className="join-logo"
            loading="lazy"
            src="/images/logo-white.png"
            alt="עומדים ביחד نقف معًا"
          />
        )}
      </div>
    </div>
  );
}

export default function JoinPage() {
  const [step, setStep] = useState('amount'); // amount | form | success
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [coverFee, setCoverFee] = useState(true); // "cover the transaction fee" checkbox, checked by default
  const [changingAmount, setChangingAmount] = useState(false); // payment page: in-place amount picker
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | error
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState({}); // per-field validation errors
  const [igSubmitted, setIgSubmitted] = useState(false); // member info POSTed -> show hosted card iframe
  const [openTerms, termsDialog] = useConsentTermsDialog();
  const igFormRef = useRef(null);
  // Latest donor snapshot for the mount-time ig-donation-complete handler, whose
  // closure would otherwise capture the empty initial form/amount.
  const latestDonor = useRef({});
  latestDonor.current = { form, amount, currency };
  // Guards against the ig-donation-complete message (and its tracking calls)
  // being processed twice for the same donation, e.g. if the iframe delivers
  // it more than once.
  const completedDonationRef = useRef(false);
  // Per-attempt id linking the 'initiated' donor details to the completion
  // that comes back through IsraelGives' successurl. Set in onSubmit.
  const paymentTokenRef = useRef('');
  const [slideBack, setSlideBack] = useState(false); // animate the amount page sliding in from the left
  const [faqOpen, setFaqOpen] = useState(false); // the "what does it mean to be a member" panel
  const [openTab, setOpenTab] = useState(-1); // which accordion tab inside that panel is open
  // Pick the portrait video on mobile, the landscape one on desktop (kept in sync on resize).
  const [mobileVideo, setMobileVideo] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  // The thank-you/share dialog opens on a completed donation (see the
  // ig-donation-complete handler).
  const [showThankYou, setShowThankYou] = useState(false);

  // Once completed, stays suppressed even if the user clicks "back" to the amount
  // step afterward (step no longer 'success') while the donor fields are still
  // populated — otherwise closing the tab re-triggers an abandoned-form send for
  // an already-successful donation.
  const { sendNow: sendAbandonedForm } = useAbandonedForm({ form, page: 'הצטרפות', donationType: 'חודשי', amount, submitted: completedDonationRef.current, activity: igSubmitted });

  // Lets the hosted payment frame grow to fit its content (see the hook).
  const igFrameRef = useIgFrameHeight();
  useBitRedirect(); // mobile bit: their framed page hands us the link to open

  // Always land at the top on load/refresh, ignoring the browser's restored scroll position.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  // A donor who paid through a method that LEFT the iframe (PayPal) never got
  // the in-page success flow — ig-complete.html sends them back here with
  // ?donated=1 instead. Their donation is already recorded server-side by then;
  // this is purely the confirmation they'd otherwise never see.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('donated') !== '1') return;
    completedDonationRef.current = true; // never chase them as an abandoned form
    setShowThankYou(true);
    url.searchParams.delete('donated'); // so a refresh doesn't reopen the dialog
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  // Swap the join video source when crossing the mobile breakpoint.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setMobileVideo(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // The IsraelGives frame redirects to /ig-complete.html (same-origin) on a
  // successful charge, which postMessages us. Swap to our own success screen.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'ig-donation-complete') {
        // Answer first, before any early return: ig-complete.html takes the
        // whole tab back to the campaign if nothing acknowledges it, and a
        // duplicate message is still proof that this page is handling it.
        if (e.source) e.source.postMessage({ type: 'ig-donation-ack' }, window.location.origin);
        if (completedDonationRef.current) return;
        completedDonationRef.current = true;
        // The completion is reported to the backend by ig-complete.html, not
        // here — that's the only path that also survives a payment method
        // which left the iframe (PayPal). This handler owns the UI and the
        // conversions; duplicating the report would just be a second write.
        // The currency the member chose, not a hardcoded ILS — read off the ref
        // because this handler is mounted once and its closure would otherwise
        // hold the initial value.
        const paidCurrency = latestDonor.current.currency || DEFAULT_CURRENCY;
        const paidValue = resolvePurchaseValue(latestDonor.current.total, e.data.sum);
        if (window.fbq) window.fbq('track', 'Purchase', { value: paidValue, currency: paidCurrency });
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-718777808/kPpUCPHXhu0bENDb3tYC', transaction_id: e.data.donationId || '' });
        }
        // GA4 + the other GTM purchase tags — see analytics.js for why this isn't a gtag() call.
        trackPurchase({ transactionId: e.data.donationId, value: paidValue, currency: paidCurrency });
        setIgSubmitted(false);
        setStatus('idle');
        setStep('success');
        setShowThankYou(true); // open the share dialog over the success screen
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Once member details pass validation, POST the hidden form into the frame so
  // IsraelGives shows the hosted card fields.
  useEffect(() => {
    if (igSubmitted && igFormRef.current) igFormRef.current.submit();
  }, [igSubmitted]);

  const joinVideoSrc = mobileVideo ? '/videos/join_mobile.mp4' : '/videos/join_desktop.mp4';

  // Fee is computed off the chosen amount but only added to the sum sent to
  // IsraelGives when the member opts in — the displayed amount stays as chosen.
  const feeAmount = calcFeeCoverAmount(amount, currency);
  const totalAmount = coverFee ? Math.round((Number(amount) + feeAmount) * 100) / 100 : Number(amount);
  latestDonor.current.total = totalAmount;

  // Presets differ per currency, so a selection made in one doesn't carry over.
  const selectCurrency = (code) => {
    setCurrency(code);
    setAmount('');
  };

  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const chooseAmount = (value) => {
    if (!value || Number(value) < currencyMeta(currency).min) return;
    setAmount(String(value));
    // On mobile the amount buttons sit in the fixed bottom panel. If the page is
    // scrolled down, smoothly glide to the top and only swap in the form once we
    // arrive, so the content change doesn't cut the animation short.
    const onMobile = window.matchMedia('(max-width: 860px)').matches;
    if (onMobile && window.scrollY > 0) {
      let advanced = false;
      const advance = () => {
        if (advanced) return;
        advanced = true;
        window.removeEventListener('scrollend', advance);
        setStep('form');
      };
      window.addEventListener('scrollend', advance);
      setTimeout(advance, 600); // fallback for browsers without 'scrollend'
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStep('form');
    }
  };

  // Payment page: pick a new amount in place (no navigation back to the amount page).
  const pickAmount = (value) => {
    if (!value || Number(value) < currencyMeta(currency).min) return;
    setAmount(String(value));
    setChangingAmount(false);
  };

  // Shared "back" button for both the mid-form step (still filling in) and the
  // post-success screen. Only clear the donor fields in the post-success case
  // (completedDonationRef stays true forever once set) — mid-form, a user
  // changing the amount shouldn't lose what they've already typed.
  const backToAmount = () => {
    sendAbandonedForm(); // capture partial data before leaving the form step
    setChangingAmount(false);
    setIgSubmitted(false); // drop the hosted-payment frame if it was showing
    setAmount(''); // don't carry the selection back to the join page's amount grid
    setSlideBack(true);
    if (completedDonationRef.current) setForm(emptyForm);
    setStep('amount');
  };

  // Submitting the member form no longer charges a card here — once the donor
  // details pass validation it hands them to the IsraelGives hosted iframe (see
  // renderHostedPayment), which collects the card and processes the payment.
  const onSubmit = (e) => {
    e.preventDefault();
    const found = validateForm(form, currency);
    setErrors(found);
    if (Object.keys(found).length) return; // block submit on validation errors
    setErrorMsg('');
    // One token per attempt, generated here and reused by the hidden POST form
    // below — it's what lets the backend match this donor to the completion.
    paymentTokenRef.current = newPaymentToken();
    logDonationEvent({
      stage: 'initiated', page: 'הצטרפות', campaignName: 'join',
      token: paymentTokenRef.current,
      firstName: form.firstName, lastName: form.lastName, email: form.email,
      phone: form.phone, city: form.city, amount, recurring: true, frequency: '2',
      // Rejoined server-side with the completion, which carries none of it.
      currency,
      street: form.address, zipCode: form.zipCode, country: countryName(form.countryId),
      ...readUtmParams(),
    });
    setIgSubmitted(true);
  };

  // Hidden POST form + iframe: IsraelGives renders the hosted card fields and
  // its own "pay" button inside the frame. Membership is a recurring monthly
  // donation; RouteName preserves the original new-member ("jraise") routing.
  const renderHostedPayment = () => {
    const { action, fields } = buildIgPayment({
      profile: form,
      amount: totalAmount,
      currency,
      frequency: '2', // recurring monthly membership
      extraFields: { RouteName: 'jraise' },
      token: paymentTokenRef.current,
      returnPath: window.location.pathname, // where to land a donor who came back off-frame
    });
    return (
      <div className="mf-hosted">
        <form ref={igFormRef} method="POST" action={action} target="ig-frame" className="mf-hosted-post">
          {Object.entries(fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
        <iframe ref={igFrameRef} className="mf-iframe" title="IsraelGives" name="ig-frame" />
        <p className="member-consent">
          <span className="bi-he">{j.consent.he}<TermsLink onOpen={openTerms}>{j.consent.heTerms}</TermsLink></span>
          <span className="bi-ar">{j.consent.ar}<TermsLink onOpen={openTerms}>{j.consent.arTerms}</TermsLink></span>
        </p>
        <button type="button" className="mf-hosted-edit" onClick={() => setIgSubmitted(false)}>
          <span className="bi-he">חזרה</span>{' '}
          <span className="bi-ar">رجوع</span>
        </button>
      </div>
    );
  };

  if (step === 'form' || step === 'success') {
    return (
      <div className="member-screen">
        <ThankYouModal open={showThankYou} onClose={() => setShowThankYou(false)} variant="join" />
        {termsDialog}

        <button type="button" className="member-back" onClick={backToAmount}>
          <span aria-hidden="true">→ </span>
          <span className="bi-he">חזרה</span>
          <span className="bi-ar">رجوع</span>
        </button>

        <div className={`member-grid${changingAmount ? ' member-grid--picking' : ''}`}>
          {/* right: logo + heading + form/success */}
          <div className="member-main">
            <a className="member-logo" href="/">
              <img
                src="/images/logo-purple.png"
                alt="עומדים ביחד نقف معًا"
              />
            </a>
            <h1 className="member-heading">
              <span className="bi-he">{j.heading.he}</span>
              <span className="bi-ar">{j.heading.ar}</span>
            </h1>

            {step === 'form' ? (
              <form className="member-form" onSubmit={onSubmit} noValidate>
                {/* Donor details — locked once submitted to the hosted iframe. */}
                <fieldset className="mf-lock" disabled={igSubmitted}>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.firstName.he} {j.fields.firstName.ar} <em>*</em></span>
                  <input autoComplete="given-name" className={errors.firstName ? 'mf-invalid' : undefined} value={form.firstName} onChange={setField('firstName')} />
                  {errors.firstName && <span className="mf-error">{errors.firstName}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.lastName.he} {j.fields.lastName.ar} <em>*</em></span>
                  <input autoComplete="family-name" className={errors.lastName ? 'mf-invalid' : undefined} value={form.lastName} onChange={setField('lastName')} />
                  {errors.lastName && <span className="mf-error">{errors.lastName}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.address.he} {j.fields.address.ar} <em>*</em></span>
                  <input autoComplete="street-address" className={errors.address ? 'mf-invalid' : undefined} value={form.address} onChange={setField('address')} />
                  {errors.address && <span className="mf-error">{errors.address}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.city.he} {j.fields.city.ar} <em>*</em></span>
                  <input autoComplete="address-level2" className={errors.city ? 'mf-invalid' : undefined} value={form.city} onChange={setField('city')} />
                  {errors.city && <span className="mf-error">{errors.city}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.phone.he} {j.fields.phone.ar} <em>*</em></span>
                  <input type="tel" autoComplete="tel" className={errors.phone ? 'mf-invalid' : undefined} value={form.phone} onChange={setField('phone')} />
                  {errors.phone && <span className="mf-error">{errors.phone}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.email.he} {j.fields.email.ar} <em>*</em></span>
                  <input type="email" autoComplete="email" className={errors.email ? 'mf-invalid' : undefined} value={form.email} onChange={setField('email')} />
                  {errors.email && <span className="mf-error">{errors.email}</span>}
                </label>
                <label className="mf-field">
                  <span className="mf-label">{j.fields.birthYear.he} {j.fields.birthYear.ar} <em>*</em></span>
                  <select autoComplete="bday-year" value={form.birthYear} onChange={setField('birthYear')}>
                    <option value="" disabled></option>
                    {BIRTH_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>

                <label className="mf-check">
                  <input
                    type="checkbox"
                    checked={form.isStudent}
                    onChange={(e) => setForm((f) => ({ ...f, isStudent: e.target.checked }))}
                  />
                  <span>{j.fields.student.he} {j.fields.student.ar}</span>
                </label>
                {form.isStudent && (
                  <div className="mf-row">
                    <label className="mf-field">
                      <span className="mf-label">{j.fields.institution.he} {j.fields.institution.ar} <em>*</em></span>
                      <input autoComplete="organization" className={errors.institution ? 'mf-invalid' : undefined} value={form.institution} onChange={setField('institution')} />
                      {errors.institution && <span className="mf-error">{errors.institution}</span>}
                    </label>
                  </div>
                )}

                {needsAddress(currency) && (
                  <ForeignAddressFields
                    form={form}
                    errors={errors}
                    setField={setField}
                    /* the street address is already asked for above, in every currency */
                    includeStreet={false}
                  />
                )}
                </fieldset>

                <div className="mf-divider" aria-hidden="true">
                  <span className="mf-divider-dot" />
                </div>

                {!igSubmitted ? (
                  <>
                    {status === 'error' && <p className="join-error member-error">{errorMsg}</p>}
                    <div className="member-secure">
                      <img className="member-secure-badge" src="/images/pci-dss.webp" alt="PCI DSS Compliant" width="130" loading="lazy" />
                      <p className="member-secure-text">
                        <span className="bi-he">{j.secureNotice.he}</span>
                        <span className="bi-ar">{j.secureNotice.ar}</span>
                      </p>
                    </div>
                    <label className="mf-check mf-cover-fee">
                      <input
                        type="checkbox"
                        checked={coverFee}
                        onChange={(e) => setCoverFee(e.target.checked)}
                      />
                      <span>
                        <span className="bi-he">{j.coverFee.he.replace('{amount}', formatAmount(feeAmount, currency))}</span>
                        <span className="bi-ar">{j.coverFee.ar.replace('{amount}', formatAmount(feeAmount, currency))}</span>
                      </span>
                    </label>
                    <button className="member-submit" type="submit">
                      {`${j.send.he} ${j.send.ar}`}
                    </button>
                    <p className="member-consent">
                      <span className="bi-he">{j.consent.he}<TermsLink onOpen={openTerms}>{j.consent.heTerms}</TermsLink></span>
                      <span className="bi-ar">{j.consent.ar}<TermsLink onOpen={openTerms}>{j.consent.arTerms}</TermsLink></span>
                    </p>
                  </>
                ) : (
                  renderHostedPayment()
                )}
              </form>
            ) : (
              <div className="member-success">
                <div className="success-check" aria-hidden="true">✓</div>
                <h2>
                  <span className="bi-he">{j.thanks.he}</span>
                  <span className="bi-ar">{j.thanks.ar}</span>
                </h2>
                <p>
                  <span className="bi-he">{j.thanksBody.he}</span>
                  <span className="bi-ar">{j.thanksBody.ar}</span>
                </p>
              </div>
            )}
          </div>

          {/* left: selected-amount summary card, or the in-place amount picker */}
      <aside className={`member-summary${changingAmount ? ' member-summary--picking' : ''}`}>
            {changingAmount ? (
              <div className="amount-picker" key="amount-picker">
                <div className="join-widget">
                  <h1 className="join-heading">
                    <span className="bi-he">{j.heading.he}</span>
                    <span className="bi-ar">{j.heading.ar}</span>
                  </h1>
                  <p className="join-q">
                    <span className="bi-he">{j.amountQuestion.he}</span>
                    <span className="bi-ar">{j.amountQuestion.ar}</span>
                  </p>
                  <AmountSelector
                    id="join-currency-inline"
                    selected={amount}
                    onChoose={pickAmount}
                    currency={currency}
                    onCurrencyChange={selectCurrency}
                  />
                  <p className="join-min">
                    <span className="bi-he">{j.minNote.he}</span>
                    <span className="bi-ar">{j.minNote.ar}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="member-summary-card">
                <p className="summary-label">
                  <span className="bi-he">{j.chosen.he}</span>
                  <span className="bi-ar">{j.chosen.ar}</span>
                </p>
                <div className="summary-value">{formatAmount(amount, currency)}</div>
                <button type="button" className="summary-change" onClick={() => { setChangingAmount(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  {j.changeAmount.he} {j.changeAmount.ar}
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className={`join${slideBack ? ' join--slide-in' : ''}`}>
      <ThankYouModal open={showThankYou} onClose={() => setShowThankYou(false)} variant="join" />

      {/* Purple logo mark shown alone where the panel will land, before it slides in. */}
      <img className="join-preload-logo" src="/images/logo-purple.png" alt="" aria-hidden="true" />

      {/* ---- Fixed pink donation panel (left) ---- */}
      <aside className="join-panel">
        <div className="join-panel-inner">
          {step === 'amount' && (
            <div className="join-widget">
              <h1 className="join-heading">
                <span className="bi-he">{j.heading.he}</span>
                <span className="bi-ar">{j.heading.ar}</span>
              </h1>
              <p className="join-q">
                <span className="bi-he">{j.amountQuestion.he}</span>
                <span className="bi-ar">{j.amountQuestion.ar}</span>
              </p>

              <AmountSelector
                id="join-currency"
                selected={amount}
                onChoose={chooseAmount}
                currency={currency}
                onCurrencyChange={selectCurrency}
              />

              <p className="join-min">
                <span className="bi-he">{j.minNote.he}</span>
                <span className="bi-ar">{j.minNote.ar}</span>
              </p>
            </div>
          )}

        </div>
      </aside>

      {/* ---- Scrolling right column: video, then explanatory content ---- */}
      <div className="join-scroll">
        <div className="join-media">
          <video
            key={joinVideoSrc}
            className="join-video"
            autoPlay
            muted
            loop
            playsInline
            poster="/images/join-poster.jpg"
          >
            <source src={joinVideoSrc} type="video/mp4" />
          </video>
        </div>

        <section className="join-content">
          <div className="join-content-inner">
            {j.sections.map((s, i) => {
              if (s.variant === 'intro') {
                /* Intro: full Hebrew block, then full Arabic block (like comp-mlxj6znv) */
                return (
                  <div className="join-block join-block--intro" key={i}>
                    <h3>{withEmphasis(s.title.he)}</h3>
                    <p>{renderText(s.body.he)}</p>
                    <div className="intro-ar">
                      <p>
                        {withEmphasis(s.title.ar)}
                        <br />
                        {renderText(s.body.ar)}
                      </p>
                    </div>
                  </div>
                );
              }

              if (s.variant === 'bar') {
                /* Clickable bar that expands an inline panel: a grid of member
                   cards followed by an accordion of explanatory tabs. */
                return (
                  <div
                    className={`join-block join-block--faq${faqOpen ? ' join-block--faq-open' : ''}`}
                    key={i}
                  >
                    <button
                      type="button"
                      className="join-faq-toggle"
                      aria-expanded={faqOpen}
                      onClick={() => setFaqOpen((o) => !o)}
                    >
                      <span className="join-faq-toggle-text">
                        <span className="bi-he">{s.title.he}</span>
                        <span className="bi-ar">{s.title.ar}</span>
                      </span>
                      <span className="join-faq-chevron" aria-hidden="true" />
                    </button>

                    {faqOpen && (
                      <div className="join-faq-panel">
                        <button
                          type="button"
                          className="join-faq-close"
                          aria-label="סגירה إغلاق"
                          onClick={() => setFaqOpen(false)}
                        >
                          ✕
                        </button>
                        <h3 className="join-faq-panel-title">
                          <span className="bi-he">{s.title.he}</span>
                          <span className="bi-ar">{s.title.ar}</span>
                        </h3>

                        <div className="join-member-cards">
                          {s.cards.map((c, ci) => (
                            <div className="join-member-card" key={ci}>
                              <img className="join-member-illo" src={c.img} alt="" loading="lazy" />
                              <p>
                                <span className="bi-he">
                                  <span className="card-lead">{c.lead.he}</span> {c.body.he}
                                </span>
                                <span className="bi-ar">
                                  <span className="card-lead">{c.lead.ar}</span> {c.body.ar}
                                </span>
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="join-faq-tabs">
                          {s.tabs.map((t, ti) => {
                            const open = openTab === ti;
                            return (
                              <div
                                className={`join-faq-tab${open ? ' join-faq-tab--open' : ''}`}
                                key={ti}
                              >
                                <button
                                  type="button"
                                  className="join-faq-tab-head"
                                  aria-expanded={open}
                                  onClick={() => setOpenTab(open ? -1 : ti)}
                                >
                                  <span className="join-faq-chevron" aria-hidden="true" />
                                  <span className="join-faq-tab-title">
                                    <span className="bi-he">{t.title.he}</span>
                                    <span className="bi-ar">{t.title.ar}</span>
                                  </span>
                                </button>
                                {open && (
                                  <div className="join-faq-tab-body">
                                    {t.logo && (
                                      <img
                                        className="join-faq-tab-logo"
                                        src={t.logo}
                                        alt="עומדים ביחד نقف معًا"
                                        loading="lazy"
                                      />
                                    )}
                                    <p>
                                      <span className="bi-he">{renderText(t.body.he)}</span>
                                      <span className="bi-ar">{renderText(t.body.ar)}</span>
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              /* variant 'box': bordered white card */
              return (
                <div className="join-block join-block--box" key={i}>
                  <h3>
                    <span className="bi-he">{withEmphasis(s.title.he)}</span>
                    <span className="bi-ar">{withEmphasis(s.title.ar)}</span>
                  </h3>
                  {s.body.he && (
                    <p>
                      <span className="bi-he">{withEmphasis(s.body.he)}</span>
                      <span className="bi-ar">{withEmphasis(s.body.ar)}</span>
                    </p>
                  )}
                </div>
              );
            })}

            <ul className="exemption-list">
              <li>
                {j.exemptionNote.he}
                <a href={`mailto:${j.exemptionNote.email}`}>{j.exemptionNote.email}</a>
              </li>
              <li>
                {j.exemptionNote.ar}
                <a href={`mailto:${j.exemptionNote.email}`}>{j.exemptionNote.email}</a>
              </li>
            </ul>
          </div>
        </section>

        <a
          className="join-bottom-logo"
          href="https://www.standing-together.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            loading="lazy"
            src="/images/logo-purple.png"
            alt="עומדים ביחד - نقف معًا"
            width="195"
            height="115"
            style={{ objectFit: 'contain' }}
          />
        </a>
      </div>
    </div>
  );
}
